if (!customElements.get('bookmark-bundle-picker')) {
  customElements.define('bookmark-bundle-picker', class BookmarkBundlePicker extends HTMLElement {

    connectedCallback() {
      this.basePrice  = parseInt(this.dataset.basePrice) || 18000;
      this.formId     = this.dataset.formId;
      this.form       = document.getElementById(this.formId);

      /* ── State ── */
      this.tier       = { qty: 1, price: 0, title: '', variantId: '' };
      this.picked     = {};
      this.submitting = false;

      /* ── DOM refs ── */
      this.packCards     = [...this.querySelectorAll('.bbp__pack-card')];
      this.designCards   = [...this.querySelectorAll('.bbp__design-card')];
      this.atcBtns       = [...this.querySelectorAll('.bbp__atc-btn')];
      this.autofillBtn   = this.querySelector('.bbp__autofill-btn');
      this.progressFill  = this.querySelector('.bbp__progress-fill');
      this.designsHint   = this.querySelector('.bbp__designs-hint');
      this.stickyBar     = this.querySelector('.bbp__sticky');
      this.ctaCard       = this.querySelector('.bbp__cta-card');
      this.hiddenProps   = this.querySelector('#bbp-hidden-props');

      /* shared display spans */
      this.displayPrices  = [...this.querySelectorAll('.bbp__display-price, .bbp__sticky-price')];
      this.displayMrps    = [...this.querySelectorAll('.bbp__display-mrp, .bbp__sticky-mrp')];
      this.displayNotes   = [...this.querySelectorAll('.bbp__display-note')];
      this.displaySaves   = [...this.querySelectorAll('.bbp__display-save')];
      this.remainingSpans = [...this.querySelectorAll('.bbp__remaining')];
      this.ctaLabels      = [...this.querySelectorAll('.bbp__cta-label')];
      this.stickyHint     = this.querySelector('.bbp__sticky-hint');
      this.stickyLabel    = this.querySelector('.bbp__sticky-label');

      this._bindPacks();
      this._bindDesigns();
      this._bindActions();
      this._bindStickyObserver();

      /* Prime with first pack card */
      if (this.packCards[0]) this._selectPack(this.packCards[0]);
    }

    /* =============================================================
       PACK SELECTION
       ============================================================= */
    _bindPacks() {
      this.packCards.forEach(card => {
        card.addEventListener('click', () => this._selectPack(card));
      });
    }

    _selectPack(card) {
      this.packCards.forEach(c => {
        c.classList.remove('is-active');
        c.setAttribute('aria-pressed', 'false');
      });
      card.classList.add('is-active');
      card.setAttribute('aria-pressed', 'true');

      this.tier = {
        qty:       parseInt(card.dataset.qty)      || 1,
        price:     parseInt(card.dataset.price)    || 0,
        title:     card.dataset.title              || '',
        variantId: card.dataset.variantId          || '',
      };

      this._trimPicks();
      this._refreshUI();
    }

    /* =============================================================
       DESIGN PICKING
       ============================================================= */
    _bindDesigns() {
      this.designCards.forEach(card => {
        const id     = card.dataset.designName;
        const imgBtn = card.querySelector('.bbp__design-img-btn');
        const addBtn = card.querySelector('.bbp__add-btn');
        const decBtn = card.querySelector('.bbp__dec-btn');
        const incBtn = card.querySelector('.bbp__inc-btn');

        if (imgBtn) imgBtn.addEventListener('click', () => this._inc(id));
        if (addBtn) addBtn.addEventListener('click', () => this._inc(id));
        if (decBtn) decBtn.addEventListener('click', () => this._dec(id));
        if (incBtn) incBtn.addEventListener('click', () => this._inc(id));
      });
    }

    _inc(id) {
      if (this._remaining <= 0) { this._nudgeProgress(); return; }
      this.picked[id] = (this.picked[id] || 0) + 1;
      this._refreshUI();
    }

    _dec(id) {
      if ((this.picked[id] || 0) > 0) {
        this.picked[id]--;
        if (this.picked[id] === 0) delete this.picked[id];
      }
      this._refreshUI();
    }

    _trimPicks() {
      let total = this._chosen;
      const keys = Object.keys(this.picked);
      let i = keys.length - 1;
      while (total > this.tier.qty && i >= 0) {
        const over = total - this.tier.qty;
        const cut  = Math.min(this.picked[keys[i]], over);
        this.picked[keys[i]] -= cut;
        if (this.picked[keys[i]] <= 0) delete this.picked[keys[i]];
        total -= cut;
        i--;
      }
    }

    get _chosen()    { return Object.values(this.picked).reduce((a, b) => a + b, 0); }
    get _remaining() { return Math.max(0, this.tier.qty - this._chosen); }

    /* =============================================================
       AUTOFILL — true random shuffle (Fisher-Yates)
       ============================================================= */
    _autoFill() {
      const ids = this.designCards.map(c => c.dataset.designName).filter(Boolean);
      if (!ids.length) return;

      this.picked = {};
      const qty   = this.tier.qty;

      /* Build a shuffled pool: repeat ids until we have enough, then shuffle */
      const pool = [];
      while (pool.length < qty) {
        pool.push(...ids);
      }

      /* Fisher-Yates shuffle */
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }

      /* Take the first `qty` from the shuffled pool */
      for (let k = 0; k < qty; k++) {
        const id = pool[k];
        this.picked[id] = (this.picked[id] || 0) + 1;
      }

      this._refreshUI();
    }

    /* =============================================================
       ATC + AUTOFILL BINDINGS (single _bindActions — no duplicate)
       ============================================================= */
    _bindActions() {
      this.atcBtns.forEach(btn => {
        btn.addEventListener('click', () => this._addToCart());
      });
      if (this.autofillBtn) {
        this.autofillBtn.addEventListener('click', () => this._autoFill());
      }
    }

    /* =============================================================
       ADD TO CART
       ============================================================= */
    async _addToCart() {
      if (this._remaining > 0) {
        const section = document.getElementById('bbp-designs-section');
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this._nudgeProgress();
        return;
      }
      if (this.submitting) return;
      this.submitting = true;
      this._setCtaState('loading');

      const props = {};
      Object.entries(this.picked).forEach(([name, qty]) => { props[name] = qty; });

      /* Write properties into the hidden form */
      if (this.hiddenProps) {
        this.hiddenProps.innerHTML = '';
        Object.entries(props).forEach(([k, v]) => {
          const inp = Object.assign(document.createElement('input'), { type: 'hidden', name: `properties[${k}]`, value: v });
          this.hiddenProps.appendChild(inp);
        });
      }

      try {
        const fd = new FormData(this.form);
        fd.set('id', this.tier.variantId);
        fd.set('quantity', 1);
        Object.entries(props).forEach(([k, v]) => fd.set(`properties[${k}]`, v));

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });

        if (res.ok) {
          this._setCtaState('success');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          const drawer = document.querySelector('cart-drawer');
          if (drawer?.open) drawer.open();
          setTimeout(() => { this.submitting = false; this._setCtaState('idle'); }, 3000);
        } else {
          throw new Error('Cart error');
        }
      } catch {
        this._setCtaState('error');
        setTimeout(() => { this.submitting = false; this._setCtaState('idle'); }, 2500);
      }
    }

    _setCtaState(state) {
      const map = { loading: 'Adding to bag…', success: 'Added ✓', error: 'Error — try again', idle: null };
      if (state !== 'idle') {
        const txt = map[state];
        this.ctaLabels.forEach(el => el.textContent = txt);
        if (this.stickyLabel) this.stickyLabel.textContent = txt;
      } else {
        this.submitting = false;
        this._refreshUI();
      }
    }

    /* =============================================================
       STICKY OBSERVER
       ============================================================= */
    _bindStickyObserver() {
      if (!this.ctaCard || !this.stickyBar) return;
      const obs = new IntersectionObserver(
        ([e]) => this.stickyBar.classList.toggle('is-visible', !e.isIntersecting),
        { threshold: 0 }
      );
      obs.observe(this.ctaCard);
    }

    /* =============================================================
       PROGRESS NUDGE
       ============================================================= */
    _nudgeProgress() {
      const pb = this.querySelector('.bbp__progress-bar');
      if (!pb) return;
      pb.style.outline = '2px solid #e94b7c';
      pb.style.outlineOffset = '2px';
      pb.style.borderRadius = '9999px';
      setTimeout(() => { pb.style.outline = ''; pb.style.outlineOffset = ''; }, 700);
    }

    /* =============================================================
       MONEY FORMATTER
       ============================================================= */
    _money(cents) {
      return '\u20B9' + (cents / 100).toLocaleString('en-IN');
    }

    /* =============================================================
       REFRESH ALL UI
       ============================================================= */
    _refreshUI() {
      const { qty, price, title } = this.tier;
      const chosen    = this._chosen;
      const remaining = this._remaining;
      const mrp       = this.basePrice * qty;
      const saved     = mrp - price;
      const perUnit   = Math.round(price / qty);
      const complete  = remaining === 0 && qty > 0;

      /* Prices */
      this.displayPrices.forEach(el => el.textContent = this._money(price));
      this.displayMrps.forEach(el => {
        el.textContent  = this._money(mrp);
        el.style.display = saved > 0 ? '' : 'none';
      });
      this.displayNotes.forEach(el => {
        el.textContent = `${this._money(perUnit)} per bookmark \u00B7 ${title}`;
      });
      this.displaySaves.forEach(el => {
        el.textContent   = `Save ${this._money(saved)}`;
        el.style.display = saved > 0 ? '' : 'none';
      });

      /* Remaining counts */
      this.remainingSpans.forEach(el => el.textContent = remaining);

      /* Designs hint */
      if (this.designsHint) {
        this.designsHint.innerHTML = complete
          ? `<strong style="color:#059669">Pack complete! You\u2019re ready to add to bag.</strong>`
          : `<span class="bbp__remaining">${remaining}</span> of ${qty} left to choose`;
      }

      /* Sticky hint */
      if (this.stickyHint) {
        this.stickyHint.textContent = complete
          ? 'Ready to add!'
          : `${remaining} design${remaining !== 1 ? 's' : ''} left to pick`;
      }

      /* CTA button labels */
      const ctaTxt = complete
        ? `Add ${qty} bookmark${qty !== 1 ? 's' : ''} \u2014 ${this._money(price)}`
        : `Pick ${remaining} design${remaining !== 1 ? 's' : ''} \u2192`;
      this.ctaLabels.forEach(el => el.textContent = ctaTxt);
      if (this.stickyLabel) {
        this.stickyLabel.textContent = complete ? 'Add to bag' : `Pick ${remaining} design${remaining !== 1 ? 's' : ''}`;
      }

      /* Progress bar */
      if (this.progressFill) {
        this.progressFill.style.width = qty > 0 ? `${Math.round((chosen / qty) * 100)}%` : '0%';
      }

      /* Per-card updates */
      this.designCards.forEach(card => {
        const id    = card.dataset.designName;
        const count = this.picked[id] || 0;

        const badge   = card.querySelector('.bbp__count-badge');
        const actions = card.querySelector('.bbp__design-actions');
        const addBtn  = card.querySelector('.bbp__add-btn');
        const incBtn  = card.querySelector('.bbp__inc-btn');
        const imgBtn  = card.querySelector('.bbp__design-img-btn');

        const isSelected = count > 0;
        card.classList.toggle('is-selected', isSelected);

        if (badge)   { badge.textContent  = count; badge.style.display = isSelected ? 'flex' : 'none'; }
        if (actions) { actions.style.display = isSelected ? 'flex' : 'none'; }
        if (addBtn)  { addBtn.style.display = isSelected ? 'none' : ''; addBtn.disabled = !isSelected && remaining <= 0; }
        if (incBtn)  incBtn.disabled  = remaining <= 0;
        if (imgBtn)  imgBtn.disabled  = !isSelected && remaining <= 0;
      });
    }

  });
}
